import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import classnames from 'classnames';
import moment from 'moment';
import Redirect from 'react-router/Redirect';
import merge from 'lodash.merge';
import camelCase from 'lodash.camelcase';
import CardReactFormContainer from 'card-react';
import Payment from 'payment';
import { Card, CardText } from 'react-toolbox/lib/card';
import Input from 'react-toolbox/lib/input';
import {
  TitleBar,
  DocTitle,
} from '../../../../../common/components/Layout/StandardPageTemplate/Context';
import DataTable from '../../../../../common/components/DataTable';
import ErrorList from '../../../../../common/components/ErrorList';
import { setPropByPath } from '../../../../../common/utils/property-path';
import { getDisplayPrice } from '../../../../../common/utils/format-currency';
import ValidatedInput from '../../../../../common/components/ValidatedInput';
import { RegistrationStatus } from '../RegistrationCard/RegistrationForm';
import { withLoading } from '../../../../../redux-modules/loading-overlay';

import RegistrationActions from '../../../actions/registration';
import styles from './EventPayment.css';
import tableTheme from '../tableTheme.css';
import './EventCard.css?global';
import {Button} from '../../../../../common/components';

const CreditCardType = {
  unknown: 0,
  mastercard: 1,
  visa: 2,
  amex: 3,
  dinersclub: 4,
  discover: 5,
};

const parseExpirationDate = value => {
  const splitValue = value.replace(/ /g, '').split('/');
  let year = '2000';
  if (splitValue.length > 0 && splitValue[1]) {
    const currentCentury = moment().format('YYYY').substring(0, 2);
    year = `${currentCentury}${(splitValue[1] || '00').length === 1 ? `0${splitValue[1]}` : splitValue[1] }`;
  }
  return {
    year,
    month: splitValue[0],
  };
};

class PaymentView extends React.Component {
  static promises = {
    includePromises: [
      {
        type: 'ENTITY/ENTITY_POST',
      },
    ],
  };
  constructor(props) {
    super(props);
    const { price = {}, guestPrice = {} } = props.location.state || {};

    const { guestBadgeNames = [] } = props.location.state
      ? props.location.state.registrationDetails
      : {};

    const priceAmt = price ? price.price || 0 : 0;
    const guestPriceAmt = guestPrice ? guestPrice.price || 0 : 0;
    const total = priceAmt + guestPriceAmt * guestBadgeNames.length;

    this.state = {
      form: {
        ccNumber: '',
        ccExpireDate: '',
        ccFullName: '',
        ccSecurityCode: '',
      },
      total,
      registrationStatus: RegistrationStatus.UNKNOWN,
      errors: { form: [] },
      disabled : false
    };
  }

  buildSummaryData = () => {
    const {
      price = {},
      guestPrice = {},
      registrationDetails: { guestBadgeNames = [] },
    } = this.props.location.state;
    const { total } = this.state;
    const displayPrice = getDisplayPrice(price.price);
    let data = [];
    if (!price.isTable && guestPrice && guestPrice.price) {
      const displayGuestPrice = getDisplayPrice(guestPrice.price);
      data = [
        {
          key: 'individual-price',
          label: 'Individual Seat',
          value: displayPrice,
        },
      ].concat(
        guestBadgeNames.map((_guest, index) => ({
          key: `guest-${index + 1}`,
          label: `Guest Seat #${index + 1}`,
          value: displayGuestPrice,
        }))
      );
    }
    if (price.isTable) {
      data = [
        {
          key: 'table-name',
          label: price.tableName,
          value: displayPrice,
        },
      ];
    }

    data.push({
      key: 'payment-total',
      label: 'Total',
      value: getDisplayPrice(total),
    });
    return data;
  };

  mergeState = (nextState, callback) => {
    this.setState(prevState => {
      return merge({}, prevState, nextState);
    }, callback);
  };

  handleFieldValidation = ({ message, detailedErrors = [] }) => {
    const validationErrors = detailedErrors.reduce((acc, curr) => {
      let propNameParts = curr.propertyName.split('.');
      switch (propNameParts[1].toLowerCase()) {
        case 'expirationYear'.toLowerCase():
          propNameParts = ['ccExpireDate'];
          break;

        case 'expirationMonth'.toLowerCase():
          propNameParts = ['ccExpireDate'];
          break;

        case 'creditCardNumber'.toLowerCase():
          propNameParts = ['ccNumber'];
          break;

        case 'creditCardSecurityCode'.toLowerCase():
          propNameParts = ['ccSecurityCode'];
          break;

        case 'nameOnCard'.toLowerCase():
          propNameParts = ['ccFullName'];
          break;

        default:
          break;
      }
      const propPath = propNameParts.map(camelCase).join('.');
      return setPropByPath(acc, propPath, curr.message);
    }, {});
    this.mergeState({
      errors: { form: [message], ...validationErrors },
    });
  };

  handleRegisterResponse = ({ payload: { json, resp } }) => {
    if (resp.ok) {
      this.setState(prevState => ({
        registrationStatus: RegistrationStatus.REGISTERED,
        errors: { form: [] },
      }));
    } else {
      this.setState(prevState => ({
        registrationStatus: RegistrationStatus.REGISTRATION_FAILED,
      }));
      if (resp.status === 500) {
        this.props.onServiceError();
      } else if (resp.status === 409) {
        this.setState(prevState =>
          Object.assign({}, prevState, {
            errors: {
              ...prevState.errors,
              form: ['You are already registered for this event.'],
            },
          })
        );
      } else if (resp.status === 400) {
        // show validation errors
        this.handleFieldValidation(json);
      } else {
        this.setState(prevState =>
          Object.assign({}, prevState, {
            errors: {
              ...prevState.errors,
              form: ['An error occurred while processing your payment.'],
            },
          })
        );
      }
    }
    return Promise.resolve({ json, resp });
  };

  handleSubmitPayment = evt => {
    evt.preventDefault();
    if (this.state.disabled) {
        return;
    }
    this.setState({disabled: true});

    const { location, currentUser } = this.props;
    const { registrationDetails, price } = location.state;
    const { form } = this.state;
    const { ccNumber, ccFullName, ccSecurityCode, ccExpireDate } = form;
    const expirationDate = parseExpirationDate(ccExpireDate);
    const cardType =
      CreditCardType[Payment.fns.cardType(ccNumber)] || CreditCardType.unknown;

    const registrationWithPayment = Object.assign({}, registrationDetails, {
      IndividualKey: registrationDetails.individualKey || currentUser.id,
      PriceKey: price.key,
      Payment: {
        CreditCardNumber: ccNumber.replace(/\s+/g, ''),
        NameOnCard: ccFullName,
        CreditCardSecurityCode: ccSecurityCode,
        ExpirationMonth: expirationDate.month,
        ExpirationYear: expirationDate.year,
        PaymentType: cardType,
      },
    });
    return this.props.actions.register
      .post(registrationWithPayment)
      .then(this.handleRegisterResponse);
  };

  handleInputOnChange = name => (
    value,
    { isValid = true, message = '', restrictInput } = {}
  ) => {
    return this.setState(prevState => ({
      form: {
        ...prevState.form,
        [name]: restrictInput && !isValid ? prevState.form[name] : value,
      },
      errors: {
        ...prevState.errors,
        [name]: isValid || restrictInput ? '' : message,
      },
      disabled : false,
    }));
  };

  handleInputKeyUp = name => {
    const input = document.getElementById(name);
    if (name === 'ccNumber') {
      Payment.formatCardNumber(input);
    }
    if (name === 'ccExpireDate') {
      Payment.formatCardExpiry(input);
    }
    if (name === 'ccSecurityCode') {
      Payment.formatCardCVC(input);
    }
    this.handleInputOnChange(name)(input.value);
  };

  handleBackspace = (name, event) => {
    if (event.keyCode === 8) {
      this.handleInputKeyUp(name);
    }
  };

  render() {
    const { location, match: { params } } = this.props;
    if (!(location && location.state)) {
      return <Redirect to={`/events/${params.eventId}/register`} />;
    }
    const { event = {} } = location.state;
    const { form, registrationStatus } = this.state;
    const errors = this.state.errors.form;
    return (
      <div>
        <DocTitle title="Payment | Detail | Events | NFIB" />
        <TitleBar title="Event Payment" />
        {registrationStatus === RegistrationStatus.REGISTERED && (
          <Redirect to={`/events/${event.key}/confirmation`} />
        )}
        <div className={styles.container}>
          <Card className={styles.card}>
            <CardText className={styles.cardText}>
              <DataTable
                theme={tableTheme}
                className={classnames(styles.table, {
                  [styles.national]: event.isNational,
                  [styles.state]: !event.isNational,
                })}
                renderHeader={() => (
                  <thead className={tableTheme.thead}>
                    <tr className={tableTheme.row}>
                      <th colSpan={2} className={tableTheme.cellHeading}>
                        {`${event.title}`}
                      </th>
                    </tr>
                  </thead>
                )}
                columnDefinitions={{
                  label: { render: colValue => <b>{colValue}</b> },
                  value: { render: colValue => <span>{colValue}</span> },
                }}
                data={this.buildSummaryData()}
              />
              <div id="card-wrapper" />
              <div className={styles.errorWrapper}>
                <ErrorList errors={errors} />
              </div>
              <CardReactFormContainer
                container="card-wrapper"
                formatting={false}
                defaultValues={{ name: 'Name' }}
                className={styles.ccForm}
              >
                <form onSubmit={this.handleSubmitPayment}>
                  <div className={styles.ccForm}>
                    <ValidatedInput
                      label="Name on Card"
                      type="text"
                      name="name"
                      validation={[
                        {
                          checkValid: value => value[0] !== ' ',
                          restrictInput: true,
                          events: ['onChange'],
                        },
                        {
                          pattern: /^[a-z-\s]{0,25}$/gi,
                          message:
                            'Valid names may only contain letters, hyphens, periods, and must be no longer than 25 characters.',
                          restrictInput: true,
                          events: ['onChange'],
                        },
                        {
                          checkValid: value => {
                            const names = value.split(/\s+/);
                            return (
                              names.length >= 2 &&
                              names.every((n, i) => i > 1 || n.length > 0)
                            );
                          },
                          message: 'Please enter your first and last name.',
                          events: ['onChange'],
                        },
                      ]}
                      value={form.ccFullName}
                      onChange={this.handleInputOnChange('ccFullName')}
                      id="ccFullName"
                      theme={styles}
                      className={styles.themedInput}
                      required
                      error={this.state.errors.ccFullName}
                    />
                    <Input
                      label="Card number"
                      type="text"
                      name="number"
                      value={form.ccNumber}
                      onChange={this.handleInputOnChange('ccNumber')}
                      onKeyPress={() => this.handleInputKeyUp('ccNumber')}
                      onKeyDown={event =>
                        this.handleBackspace('ccNumber', event)}
                      id="ccNumber"
                      theme={styles}
                      className={styles.themedInput}
                      required
                      error={this.state.errors.ccNumber}
                    />
                    <Input
                      label="Expiration Date"
                      type="text"
                      name="expiry"
                      value={form.ccExpireDate}
                      onChange={this.handleInputOnChange('ccExpireDate')}
                      onKeyPress={() => this.handleInputKeyUp('ccExpireDate')}
                      onKeyDown={event =>
                        this.handleBackspace('ccExpireDate', event)}
                      id="ccExpireDate"
                      theme={styles}
                      className={classnames(
                        styles.themedInput,
                        styles.expireDate
                      )}
                      hint="MM / YY"
                      maxLength={7}
                      required
                      error={this.state.errors.ccExpireDate}
                    />
                    <Input
                      label="CVV/CVC"
                      type="text"
                      name="cvc"
                      value={form.ccSecurityCode}
                      onChange={this.handleInputOnChange('ccSecurityCode')}
                      onKeyPress={() => this.handleInputKeyUp('ccSecurityCode')}
                      onKeyDown={event =>
                        this.handleBackspace('ccSecurityCode', event)}
                      id="ccSecurityCode"
                      theme={styles}
                      className={styles.themedInput}
                      required
                      error={this.state.errors.ccSecurityCode}
                    />
                    {/* Added span below to fulfill requirement to display transaction date (ENGA-49)
               At this point, we don't know the transaction date, but we will on confirmation.
               If confirmation is sufficient enough a place, remove this span
               */}
                    <span
                      style={{
                        fontSize: '1rem',
                        fontStyle: 'italic',
                        display: 'block',
                      }}
                    >
                      *If you submit this transaction before 12:00am local time,
                      it will be posted to your account on{' '}
                      {moment().format('MM/DD/YYYY')}
                      .
                    </span>
                    <br />
                    <Button disabled={this.state.disabled}
                      label="Pay now"
                      type="submit"
                    />
                  </div>
                </form>
              </CardReactFormContainer>
            </CardText>
          </Card>
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  actions: {
    register: bindActionCreators(RegistrationActions, dispatch),
  },
});

export default connect(null, mapDispatchToProps)(withLoading(PaymentView));



// WEBPACK FOOTER //
// ./src/App/events/Views/Detail/Payment/index.js